import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Briefcase, DoorOpen } from 'lucide-react';
import { useCustomerContext } from '@/lib/customer-context-store';
import { useAuth } from '@/lib/auth-store';

/**
 * CustomerContextBanner
 * ─────────────────────────────────────────────────────────────────────
 * Shown only when a super_admin / reseller_owner has entered a specific
 * customer context. The banner is sticky at the top of the portal shell
 * so there is no way to miss that you are "impersonating" a customer's
 * scope — every create dialog, list page, and mutation will operate on
 * that customer until you exit.
 *
 * Design language: a diagonal-striped primary-tinted band with a tight
 * monospace label. Distinct from the page chrome — you are NOT in your
 * normal surface. It inherits the portal's LTR / RTL direction from
 * <html dir> via `dir="inherit"`, and uses logical inset classes so the
 * close button lands on the trailing edge in RTL layouts.
 */
export function CustomerContextBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const current = useCustomerContext((s) => s.current);
  const exit = useCustomerContext((s) => s.exit);
  const user = useAuth((s) => s.user);

  // Only resellers / super-admins use the context pattern. If anything
  // else has stale state, silently ignore — the banner is inert.
  if (!current) return null;
  if (!user) return null;
  if (user.role !== 'super_admin' && user.role !== 'reseller_owner') return null;

  function handleExit() {
    // Exiting the customer is a one-level step-back: the reseller context
    // (if any) stays intact so super_admin lands on the filtered customers
    // list of the same reseller. Only exiting the reseller clears both.
    exit();
    navigate('/customers');
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-20 bg-primary text-primary-foreground shadow-[0_2px_0_0_hsl(221.2_83.2%_43%)]"
    >
      {/* Diagonal-stripe texture layer — subtle depth without image assets. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, transparent 0 8px, rgba(255,255,255,0.6) 8px 9px)',
        }}
      />
      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-3 py-2.5">
          <div className="flex items-center gap-3 min-w-0">
            <span
              aria-hidden
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-sm bg-primary-foreground/15 ring-1 ring-primary-foreground/25"
            >
              <Briefcase className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] opacity-80 leading-none">
                {t('customer.nowEditingCustomer')}
              </div>
              <div className="mt-1 text-sm font-semibold leading-tight truncate" dir="auto">
                {current.name}
                {current.customerNumber !== null && (
                  <span className="ms-2 inline-flex items-center rounded-sm bg-primary-foreground/15 px-1.5 py-0.5 text-[10px] font-mono font-semibold tracking-wider leading-none align-middle">
                    C-{current.customerNumber}
                  </span>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleExit}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-3 py-1.5 text-xs font-medium tracking-wide transition-colors hover:bg-primary-foreground/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/60 focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
            aria-label={`${t('customer.exitContext')} — ${current.name}`}
          >
            <DoorOpen className="h-3.5 w-3.5" />
            <span>{t('customer.exitContext')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
